import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import BoardCard from "./BoardCard";
import { coveredPeriods, slotKey } from "./useBoard";
import { WEEKDAY_KEY } from "../../utils/helpers/timetableDisplay";

// ---------------------------------------------------------------------------
// The week grid: rows are periods, columns are the school's teaching weekdays.
//
// Cells and cards are items of ONE CSS grid, both placed explicitly. That is
// what lets a duration>1 card render as a single element spanning its periods
// (`grid-row: n / span d`) instead of N stacked copies — and it means the empty
// cell underneath still exists as a click target.
//
// weekday follows Date.getDay() (0=Sunday…6=Saturday), matching the model and
// the rest of the app (see components/scheduling/PeriodPicker.js). Not remapped.
// ---------------------------------------------------------------------------

// getDay() index → the existing `upcoming.*` day-name keys.
const EMPTY_LANES = new Set();

const shortTime = (value) => (value ? String(value).slice(0, 5) : "");

// Cell appearance by placement state — a table, not a chain of ternaries, so the
// vocabulary stays in one place (same convention as ScheduleImport's OUTCOME_CLASS).
// It mirrors PeriodPicker: indigo = go, dashed slate = not a target, rose = clash.
const CELL_CLASS = {
  idle: "border-slate-100 bg-white",
  valid:
    "border-indigo-300 bg-indigo-50/70 hover:bg-indigo-100 hover:border-indigo-500 cursor-pointer",
  invalid: "border-dashed border-rose-200 bg-rose-50/60 cursor-not-allowed",
};

const BoardCell = memo(function BoardCell({
  weekday,
  period,
  column,
  row,
  state,
  reason,
  label,
  onClick,
}) {
  const interactive = state === "valid" || state === "invalid";
  return (
    <button
      type="button"
      disabled={!interactive}
      onClick={interactive ? () => onClick(weekday, period, state) : undefined}
      style={{ gridColumn: column, gridRow: row }}
      title={reason || undefined}
      aria-label={label}
      className={`rounded-lg border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 disabled:cursor-default ${
        CELL_CLASS[state] || CELL_CLASS.idle
      }`}
    />
  );
});

export default function BoardGrid({
  weekdays,
  periods,
  cards,
  view,
  selectedUuid,
  targetMap,
  busyUuid,
  readOnly = false,
  onCardClick,
  onCellClick,
  onToggleLock,
}) {
  const { t } = useTranslation();

  const columnOf = useMemo(() => {
    const map = new Map();
    weekdays.forEach((weekday, index) => map.set(weekday, index + 2)); // col 1 = gutter
    return map;
  }, [weekdays]);

  const rowOf = useMemo(() => {
    const map = new Map();
    periods.forEach(({ period }, index) => map.set(period, index + 2)); // row 1 = header
    return map;
  }, [periods]);

  // Cards that start in the same slot share the cell width in lanes. This only
  // ever kicks in for Teacher/Subject view, where two different classes can
  // genuinely sit in one period — Class view is single-lane by definition.
  const laneOf = useMemo(() => {
    // Bucket by every period a card COVERS, not just the one it starts in.
    // Keying on the start slot alone makes a (Mon, P1, duration 2) card and a
    // (Mon, P2, duration 1) card land in different buckets — each then believes
    // it is alone, both claim full width, and the single-period card renders on
    // top of the double's second half.
    const buckets = new Map();
    const placedCards = cards.filter(
      (card) => card.weekday != null && card.period != null,
    );
    placedCards.forEach((card) => {
      coveredPeriods(card).forEach((period) => {
        const key = slotKey(card.weekday, period);
        const bucket = buckets.get(key);
        if (bucket) bucket.push(card.uuid);
        else buckets.set(key, [card.uuid]);
      });
    });

    // A card's lane count is the widest overlap across ALL the periods it
    // touches, so a double period stays aligned with the singles beside it.
    const lanesFor = new Map();
    placedCards.forEach((card) => {
      let widest = 1;
      coveredPeriods(card).forEach((period) => {
        widest = Math.max(widest, (buckets.get(slotKey(card.weekday, period)) || []).length);
      });
      lanesFor.set(card.uuid, widest);
    });

    // Assign each card the first lane free for every period it covers.
    const taken = new Map();
    const map = new Map();
    placedCards.forEach((card) => {
      const lanes = lanesFor.get(card.uuid) || 1;
      const spanned = coveredPeriods(card);
      const isFree = (candidate) =>
        spanned.every(
          (period) => !(taken.get(slotKey(card.weekday, period)) || EMPTY_LANES).has(candidate),
        );
      let lane = 0;
      while (lane < lanes - 1 && !isFree(lane)) lane += 1;
      spanned.forEach((period) => {
        const key = slotKey(card.weekday, period);
        if (!taken.has(key)) taken.set(key, new Set());
        taken.get(key).add(lane);
      });
      map.set(card.uuid, { lane, lanes });
    });
    return map;
  }, [cards]);

  const placed = useMemo(
    () =>
      cards.filter(
        (card) =>
          card.weekday != null &&
          card.period != null &&
          columnOf.has(card.weekday) &&
          rowOf.has(card.period),
      ),
    [cards, columnOf, rowOf],
  );

  const gridStyle = {
    gridTemplateColumns: `64px repeat(${weekdays.length}, minmax(132px, 1fr))`,
    gridTemplateRows: `auto repeat(${periods.length}, minmax(58px, auto))`,
    gap: "4px",
  };

  return (
    <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
      {/* Deliberately NOT role="grid": the cells and the spanning cards are flat
          siblings of one CSS grid, so there are no real ARIA rows to expose. A
          labelled group plus per-cell/per-card labels is the honest mapping. */}
      <div
        className="min-w-[720px] grid"
        style={gridStyle}
        role="group"
        aria-label={t("timetable.gridAria")}
        aria-busy={busyUuid ? "true" : "false"}
      >
        {/* Corner + weekday headers */}
        <div style={{ gridColumn: 1, gridRow: 1 }} />
        {weekdays.map((weekday) => (
          <div
            key={`head-${weekday}`}
            style={{ gridColumn: columnOf.get(weekday), gridRow: 1 }}
            className="pb-2 text-center text-[10px] font-black uppercase tracking-widest text-slate-500"
          >
            {t(`timetable.weekday.${WEEKDAY_KEY[weekday]}`)}
          </div>
        ))}

        {/* Period gutter */}
        {periods.map(({ period, start, end }) => (
          <div
            key={`gutter-${period}`}
            style={{ gridColumn: 1, gridRow: rowOf.get(period) }}
            className="flex flex-col items-end justify-center pr-2 text-right"
          >
            <span className="text-[11px] font-black text-slate-500">P{period}</span>
            {start && (
              <span className="text-[9px] font-semibold text-slate-500 leading-tight">
                {shortTime(start)}
                {end ? `–${shortTime(end)}` : ""}
              </span>
            )}
          </div>
        ))}

        {/* Slot backgrounds — the click targets. Rendered before the cards so a
            card visually sits on top of its own cell. */}
        {weekdays.map((weekday) =>
          periods.map(({ period }) => {
            const conflicts = targetMap ? targetMap.get(slotKey(weekday, period)) : null;
            let state = "idle";
            let reason = "";
            if (conflicts) {
              if (conflicts.length === 0) {
                state = "valid";
              } else {
                state = "invalid";
                reason = conflicts
                  .map((conflict) =>
                    conflict.kind === "overflow"
                      ? t("timetable.clashOverflow", { count: Number(conflict.label) })
                      : t(
                          conflict.kind === "class"
                            ? "timetable.clashClass"
                            : "timetable.clashTeacher",
                          { name: conflict.label },
                        ),
                  )
                  .join(" · ");
              }
            }
            return (
              <BoardCell
                key={`cell-${weekday}-${period}`}
                weekday={weekday}
                period={period}
                column={columnOf.get(weekday)}
                row={rowOf.get(period)}
                state={state}
                reason={reason}
                label={t("timetable.slotAria", {
                  day: t(`timetable.weekday.${WEEKDAY_KEY[weekday]}`),
                  period,
                })}
                onClick={onCellClick}
              />
            );
          }),
        )}

        {/* Cards */}
        {placed.map((card) => {
          const lane = laneOf.get(card.uuid) || { lane: 0, lanes: 1 };
          return (
            <BoardCard
              key={card.uuid}
              card={card}
              view={view}
              selected={card.uuid === selectedUuid}
              busy={card.uuid === busyUuid}
              readOnly={readOnly}
              column={columnOf.get(card.weekday)}
              rowStart={rowOf.get(card.period)}
              lane={lane.lane}
              lanes={lane.lanes}
              onSelect={onCardClick}
              onToggleLock={onToggleLock}
            />
          );
        })}
      </div>
    </div>
  );
}
